import React, { useCallback, useEffect } from "react";
import { formatCurrency } from "../../lib/utils";
import { SuperAdminDashboardStats } from "./@types";
import CompanyRegistrationChart from "./components/CompanyRegistrationChart";
import CompanyStatusChart from "./components/CompanyStatusChart";
import StatCard from "./components/StatCard";
import SubscriptionRevenueChart from "./components/SubscriptionRevenueChart";
import { useGetSuperAdminDashboardStatsMutation } from "./dashboardApiSlice";

/* ================= Skeleton Components ================= */
const StatCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg p-6 shadow-sm">
    <div className="flex justify-between items-start mb-4">
      <div className="flex-1">
        <div className="h-3 bg-gray-200 rounded w-24 mb-3 animate-pulse" />
        <div className="h-8 bg-gray-200 rounded w-32 animate-pulse" />
      </div>
      <div className="h-12 w-16 bg-gray-200 rounded animate-pulse" />
    </div>
    <div className="h-4 bg-gray-200 rounded w-36 animate-pulse" />
  </div>
);

const ChartSkeleton: React.FC<{ height?: string }> = ({ height = "h-80" }) => (
  <div className="bg-white rounded-lg p-6 shadow-sm">
    <div className="h-5 bg-gray-200 rounded w-40 mb-6 animate-pulse" />
    <div className={`${height} bg-gray-100 rounded animate-pulse`} />
  </div>
);

/* ================= Main Dashboard Component ================= */
const Dashboard: React.FC = () => {
  const [getDashboardStats, { isLoading, data }] =
    useGetSuperAdminDashboardStatsMutation();

  const fetchDashboardStats = useCallback(async () => {
    const requestBody = {};

    try {
      await getDashboardStats(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch dashboard stats:", err);
    }
  }, [getDashboardStats]);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const response = data as SuperAdminDashboardStats | undefined;

  const getPlanLabel = (plan: string) => {
    const labels: Record<string, string> = {
      yearly: "Yearly plan",
      monthly: "Monthly plan",
      quarterly: "Quarterly plan",
      halfYear: "Half a year plan",
    };
    return labels[plan] || plan;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className="w-full mx-auto">
          {/* Stat Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <StatCardSkeleton key={i} />
            ))}
          </div>

          {/* Charts Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <ChartSkeleton height="h-96" />
            <ChartSkeleton height="h-96" />
          </div>

          {/* Bottom Section Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartSkeleton height="h-80" />
            <ChartSkeleton height="h-80" />
          </div>
        </div>
      </div>
    );
  }

  if (!response) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className="w-full mx-auto space-y-6">
          <div className="w-full">
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
            </div>
          </div>
          <div className="w-full">
            <ChartSkeleton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="w-full mx-auto">
        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total company"
            value={response.totalCompanies}
            change={response.percentChangeInCompanies}
            sparklineData={[220, 235, 242, 250, 248, 250]}
          />
          <StatCard
            title="All company vehicles"
            value={response.totalVehicles}
            change={response.percentChangeInVehicles}
            sparklineData={[3050, 3200, 3350, 3400, 3450, 3500]}
          />
          <StatCard
            title="All company drivers"
            value={response.totalDrivers}
            change={response.percentChangeInDrivers}
            sparklineData={[3670, 3650, 3580, 3520, 3500, 3480]}
          />
          <StatCard
            title="Total revenue"
            value={formatCurrency(response.totalRevenue)}
            change={response.percentChangeInRevenue}
            sparklineData={[14500, 17200, 19800, 21500, 23000, 24500]}
          />
        </div>

        {/* Subscription Revenue & Company Registration */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Subscription Revenue Chart */}
          <SubscriptionRevenueChart data={response} />
          {/* Company Registration Chart */}
          <CompanyRegistrationChart data={response} />
        </div>

        {/* Recently Added & Status Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recently Added Companies */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Recently added company
            </h3>
            <div className="space-y-4 border border-gray-200 rounded-md p-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pb-2 border-b border-gray-200 text-sm font-medium text-gray-500">
                <div>Driver assigned</div>
                <div>Registration date</div>
                <div>Subscription plan</div>
              </div>
              {response.recentCompanies.map((company, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center py-3 border-b border-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-medium">
                      {company.companyName.charAt(0)}
                    </div>
                    <span className="text-sm text-gray-900">
                      {company.companyName}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {new Date(company.registrationDate)
                      .toLocaleDateString("en-US", {
                        month: "short",
                        day: "2-digit",
                        year: "numeric",
                      })
                      .replace(",", " -")}
                  </div>
                  <div className="text-sm text-gray-900">
                    {getPlanLabel(company.currentPlan)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Companies Status Overview */}
          <CompanyStatusChart data={response} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
